import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import FeatureTip from '../../components/FeatureTip';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Upload, Loader2 } from 'lucide-react';

const Tenders = () => {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    source: '',
    file: null,
  });

  useEffect(() => {
    fetchTenders();
  }, []);

  const fetchTenders = async () => {
    try {
      const response = await api.getTenders();
      setTenders(response.data);
    } catch (error) {
      console.error('Failed to fetch tenders:', error);
      toast.error('Kunde inte hämta upphandlingar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      toast.error('Välj en PDF-fil');
      return;
    }

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('name', formData.name);
      formDataObj.append('source', formData.source);
      formDataObj.append('file', formData.file);

      await api.createTender(formDataObj);
      toast.success('Upphandling uppladdad och analyseras...');
      setDialogOpen(false);
      setFormData({ name: '', source: '', file: null });
      fetchTenders();
    } catch (error) {
      console.error('Failed to upload tender:', error);
      toast.error('Kunde inte ladda upp upphandling');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div>Laddar...</div>;
  }

  return (
    <div className="space-y-6" data-testid="tenders-page">
      <FeatureTip tipId="tenders" title="Upphandlingar (anbud)" body="Ladda upp en upphandlings-PDF så analyserar AI:n innehållet, hittar nyckeltal och föreslår en kostnadskalkyl. Sparar timmar av läsning." />
      <div className="flex justify-end">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-tender-btn" className="bg-orange-500 hover:bg-orange-600">
              <Upload size={20} className="mr-2" />
              Ladda upp upphandling
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ladda upp upphandling</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Namn</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Valfritt"
                  data-testid="tender-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="source">Källa</Label>
                <Input
                  id="source"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  placeholder="t.ex. Visma, e-Avrop"
                  data-testid="tender-source-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="file">PDF-fil *</Label>
                <Input
                  id="file"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  data-testid="tender-file-input"
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Avbryt</Button>
                <Button type="submit" disabled={uploading} data-testid="upload-tender-submit-btn" className="bg-orange-500 hover:bg-orange-600">
                  {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Laddar upp...</> : 'Ladda upp'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card rounded-xl shadow-sm border border-border">
        {tenders.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">Inga upphandlingar uppladdade</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Namn</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Källa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Skapad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Analyserad</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tenders.map((tender) => (
                  <tr key={tender.id}>
                    <td className="px-6 py-4 text-sm font-medium text-foreground">{tender.name || 'Namnlös'}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{tender.source || '-'}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(tender.created_at).toLocaleDateString('sv-SE')}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {tender.analysis_json ? (
                        <span className="text-green-600 font-medium">Ja</span>
                      ) : (
                        <span className="text-muted-foreground/60">Nej</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tenders;
